import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';

import { ActivityEventProcessType } from '@warebee/shared/import-converter';
import { cn } from '../../common/utils';
import * as KPI from '../kpi/categories';
import { inventoryKpis } from '../kpi/categories/inventory';
import { issuesKpis } from '../kpi/categories/issues';
import { labourKpis } from '../kpi/categories/labour';
import kpiMockData from '../kpi/mockData';
import WarehouseActivityCharts from './WarehouseActivityCharts';

const getKpiValue = (category: string, id: string) =>
  kpiMockData[category]?.[id]?.current ?? '-';

// Helper: flatten all KPIs from all categories
const allKpis = [
  ...KPI.processKpis,
  ...KPI.inventoryKpis,
  ...KPI.issuesKpis,
  ...KPI.labourKpis,
  ...KPI.clientsKpis,
  ...KPI.complianceKpis,
  ...KPI.dataIoKpis,
  ...KPI.insightsKpis,
  ...KPI.warehouseSummaryKpis,
];

// Group KPIs by processType
const kpisByProcess: Record<ActivityEventProcessType, typeof allKpis> =
  allKpis.reduce(
    (acc, kpi) => {
      if (!kpi.processType) return acc;
      const types = Array.isArray(kpi.processType)
        ? kpi.processType
        : [kpi.processType];
      types.forEach(type => {
        if (!acc[type]) acc[type] = [];
        acc[type].push(kpi);
      });
      return acc;
    },
    {} as Record<ActivityEventProcessType, typeof allKpis>,
  );

// KpiCard component (simple version)
const KpiCard = ({ kpi }: { kpi: any }) => (
  <Stat
    titleElement={
      <div>
        <div className="">{kpi.label}</div>
        <div className="mb-1 text-sm opacity-50">{kpi.description}</div>
      </div>
    }
    className=""
    unitOfMeasure={kpi.unit}
    value={getKpiValue(kpi.category, kpi.id)}
  >
    {/* Add value, trend, etc. as needed */}
  </Stat>
);

export const WarehouseDashboardSummary: React.FC = () => {
  const { t } = useTranslation('app');

  return (
    <Container col hasOverflowY className="gap-1">
      {/* <h2 className="mb-4 text-2xl font-bold">{t('Summary')}</h2> */}

      <StatGroup
        // className="mb-8 grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3"
        title={t('Summary')}
        classNameStatGroupItems={cn(
          `!my-0 !space-y-0 grid gap-1`,
          `grid-cols-${Math.min(allKpis.length, 3)}`,
          `md:grid-cols-${Math.min(allKpis.length, 3)}`,
          `lg:grid-cols-${Math.min(allKpis.length, 3)}`,
        )}
        className="!my-0"
      >
        {/* Inventory & Capacity */}
        <Stat
          hasHelper
          isPreview
          title={t('Inventory & Capacity')}
          value={getKpiValue('inventory', inventoryKpis[0].id)}
          unitOfMeasure={inventoryKpis[0].unit}
          icon={Icon.Box}
        >
          {inventoryKpis
            .filter(kpi => getKpiValue('inventory', kpi.id) !== '-')
            .map(kpi => (
              <StatListItem
                key={kpi.id}
                title={kpi.title}
                value={getKpiValue('inventory', kpi.id)}
                unitOfMeasure={kpi.unit}
              />
            ))}
        </Stat>

        {/* Issues */}
        <Stat
          hasHelper
          isPreview
          title={t('Issues')}
          value={getKpiValue('issues', issuesKpis[0].id)}
          icon={Icon.AlertTriangle}
        >
          {issuesKpis
            .filter(kpi => getKpiValue('issues', kpi.id) !== '-')
            .map(kpi => (
              <StatListItem
                key={kpi.id}
                title={kpi.title}
                value={getKpiValue('issues', kpi.id)}
                unitOfMeasure={kpi.unit}
              />
            ))}
        </Stat>

        {/* Performance & Labor */}
        <Stat
          hasHelper
          isPreview
          title={t('Performance & Labor')}
          value={getKpiValue('labour', labourKpis[0].id)}
          unitOfMeasure={labourKpis[0].unit}
          icon={Icon.Users}
        >
          {labourKpis
            .filter(kpi => getKpiValue('labour', kpi.id) !== '-')
            .map(kpi => (
              <StatListItem
                key={kpi.id}
                title={kpi.title}
                value={getKpiValue('labour', kpi.id)}
                unitOfMeasure={kpi.unit}
              />
            ))}
        </Stat>
      </StatGroup>

      {/* Process/Activity Breakdown */}
      <div className="w-full flex-1">
        <WarehouseActivityCharts />
      </div>
    </Container>
  );
};

export default WarehouseDashboardSummary;
